import { useMemo, useState } from 'react';
import { COLORS, SHADOWS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import type { KeycloakUser } from '../api/keycloak-admin';

/** One assignable app, keyed by its `app:<slug>` realm role. */
export interface AppOption {
  roleName: string;
  slug: string;
  description: string;
}

interface ManageAppsModalProps {
  user: KeycloakUser;
  apps: AppOption[];
  /** Role names (`app:<slug>`) currently assigned to the user. */
  assignedRoleNames: string[];
  isDark: boolean;
  colors: ColorTheme;
  isSaving: boolean;
  /** Commit the staged diff as one operation. Both arrays hold role names. */
  onSave: (added: string[], removed: string[]) => void;
  onClose: () => void;
}

/**
 * Staged app-assignment editor. Toggles do not write until Save, which commits
 * the full diff in one operation. Removing access is confirmed by name first;
 * additions commit without extra confirmation. Cancel discards all staging.
 */
export function ManageAppsModal({
  user, apps, assignedRoleNames, isDark, colors, isSaving, onSave, onClose,
}: ManageAppsModalProps): React.JSX.Element {
  const initial = useMemo(() => new Set(assignedRoleNames), [assignedRoleNames]);
  const [staged, setStaged] = useState<Set<string>>(() => new Set(assignedRoleNames));
  const [search, setSearch] = useState('');
  const [confirmingRemovals, setConfirmingRemovals] = useState<AppOption[] | null>(null);

  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.username;

  const sorted = useMemo(
    () => [...apps].sort((a, b) => a.slug.localeCompare(b.slug)),
    [apps],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(a => a.slug.toLowerCase().includes(q) || a.description.toLowerCase().includes(q));
  }, [sorted, search]);

  function toggle(roleName: string): void {
    setStaged(prev => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  }

  function computeDiff(): { added: string[]; removed: string[] } {
    const added: string[] = [];
    const removed: string[] = [];
    for (const a of apps) {
      const was = initial.has(a.roleName);
      const now = staged.has(a.roleName);
      if (now && !was) added.push(a.roleName);
      if (!now && was) removed.push(a.roleName);
    }
    return { added, removed };
  }

  function handleSaveClick(): void {
    const { added, removed } = computeDiff();
    if (removed.length > 0) {
      setConfirmingRemovals(apps.filter(a => removed.includes(a.roleName)));
      return;
    }
    onSave(added, removed);
  }

  function handleConfirmRemovals(): void {
    const { added, removed } = computeDiff();
    onSave(added, removed);
  }

  const inputStyle: React.CSSProperties = {
    height: '38px', padding: '0 12px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };

  const secondaryBtn: React.CSSProperties = {
    height: '34px', padding: '0 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent', color: colors.text, fontFamily: FONTS.heading,
    fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase',
    cursor: isSaving ? 'not-allowed' : 'pointer',
  };

  const primaryBtn: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em',
    textTransform: 'uppercase', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.6 : 1,
  };

  const eyebrowStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary,
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50 }} />

      <div className="vx-modal-fit" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: '480px', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        backgroundColor: colors.card, borderRadius: '12px',
        boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
        zIndex: 51,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '24px 24px 16px' }}>
          <div>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: '14px', letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.text, margin: '0 0 4px' }}>
              Manage apps
            </h2>
            <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>{fullName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: colors.textSecondary }} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {confirmingRemovals ? (
          /* Removal confirmation */
          <div style={{ padding: '4px 24px 20px', overflowY: 'auto' }}>
            <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text, margin: '0 0 12px' }}>
              Removing access to {confirmingRemovals.length === 1 ? 'this app' : 'these apps'} for {fullName}:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {confirmingRemovals.map(a => (
                <span key={a.roleName} style={{
                  padding: '4px 10px', borderRadius: '6px', fontFamily: FONTS.mono, fontSize: '12px',
                  color: '#EF4444', backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}>{a.slug}</span>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Search */}
            <div style={{ padding: '0 24px 16px' }}>
              <input
                type="text"
                placeholder="Search apps"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* App list */}
            <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
              {filtered.map(app => {
                const on = staged.has(app.roleName);
                return (
                  <label key={app.roleName} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
                    padding: '10px 0', borderBottom: `1px solid ${colors.border}`, cursor: 'pointer',
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: FONTS.mono, fontSize: '13px', color: colors.text }}>{app.slug}</div>
                      {app.description && (
                        <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                          {app.description}
                        </div>
                      )}
                    </div>
                    <span
                      role="switch"
                      aria-checked={on}
                      onClick={e => { e.preventDefault(); toggle(app.roleName); }}
                      style={{
                        position: 'relative', width: '36px', height: '20px', flexShrink: 0, borderRadius: '999px',
                        backgroundColor: on ? COLORS.accent.blue : (isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt),
                        border: `1px solid ${on ? COLORS.accent.blue : colors.border}`, transition: 'background-color 0.15s',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: '2px', left: on ? '17px' : '2px', width: '14px', height: '14px',
                        borderRadius: '50%', backgroundColor: on ? '#FFFFFF' : colors.textSecondary, transition: 'left 0.15s',
                      }} />
                    </span>
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>
                  No apps match your search
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
          padding: '16px 24px', borderTop: `1px solid ${colors.border}`,
        }}>
          {confirmingRemovals ? (
            <>
              <button onClick={() => setConfirmingRemovals(null)} disabled={isSaving} style={secondaryBtn}>Back</button>
              <button onClick={handleConfirmRemovals} disabled={isSaving} style={primaryBtn}>
                {isSaving ? 'Saving...' : 'Confirm changes'}
              </button>
            </>
          ) : (
            <>
              <span style={eyebrowStyle}>{staged.size} of {apps.length} assigned</span>
              <div style={{ display: 'flex', gap: '9px' }}>
                <button onClick={onClose} disabled={isSaving} style={secondaryBtn}>Cancel</button>
                <button onClick={handleSaveClick} disabled={isSaving} style={primaryBtn}>
                  {isSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
